import { useEffect, useRef, useState } from 'react';
import { getLotDetail } from '../../services/lotsService';
import { pickNonEmptyBoundary } from '../utils/enrichSharedLotsForMap';

const CONCURRENCY = 5;
const FLUSH_EVERY = 8;

const boundaryByLotId = new Map();
const failedLotIds = new Set();
const inflightLotIds = new Set();

/**
 * Baja perímetros catastrales en segundo plano para miniaturas de lista.
 * Llama `/getalllots` **sin JWT**: geometría pública, no consume la cuota
 * diaria de detalle.
 *
 * @param {string[]} lotIds
 * @returns {Map<string, Array<{ lat: number, lng: number }>>}
 */
export function usePrefetchLotBoundaries(lotIds) {
  const [byId, setById] = useState(() => new Map(boundaryByLotId));
  const queueRef = useRef([]);
  const activeRef = useRef(0);
  const pendingFlushRef = useRef(0);
  const mountedRef = useRef(true);

  const idsKey = (Array.isArray(lotIds) ? lotIds : [])
    .map((id) => String(id || '').trim())
    .filter(Boolean)
    .sort()
    .join(',');

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!idsKey) return undefined;
    const ids = idsKey.split(',');
    ids.forEach((id) => {
      if (boundaryByLotId.has(id) || inflightLotIds.has(id) || failedLotIds.has(id)) return;
      if (!queueRef.current.includes(id)) queueRef.current.push(id);
    });

    const flush = () => {
      pendingFlushRef.current = 0;
      if (!mountedRef.current) return;
      setById(new Map(boundaryByLotId));
    };

    const pump = () => {
      while (activeRef.current < CONCURRENCY && queueRef.current.length > 0) {
        const id = queueRef.current.shift();
        if (!id) continue;
        if (boundaryByLotId.has(id) || inflightLotIds.has(id) || failedLotIds.has(id)) continue;
        inflightLotIds.add(id);
        activeRef.current += 1;
        void getLotDetail(id, { auth: false, retryOn429: 1 })
          .then((lotData) => {
            const boundary = pickNonEmptyBoundary(lotData?.boundary, lotData?.lot_boundary);
            if (boundary) {
              boundaryByLotId.set(id, boundary);
              pendingFlushRef.current += 1;
            } else {
              failedLotIds.add(id);
            }
          })
          .catch(() => {
            failedLotIds.add(id);
          })
          .finally(() => {
            inflightLotIds.delete(id);
            activeRef.current -= 1;
            const shouldFlush = pendingFlushRef.current >= FLUSH_EVERY
              || (queueRef.current.length === 0 && activeRef.current === 0);
            if (shouldFlush) flush();
            if (mountedRef.current) pump();
          });
      }
    };

    const alreadyCached = ids.some((id) => boundaryByLotId.has(id));
    if (alreadyCached) flush();
    pump();
    return undefined;
  }, [idsKey]);

  return byId;
}
